import { useTranslation } from 'react-i18next';

import { AssignedPermission, PermissionModules } from 'types/rolesTypes';
import RolePermissionsTable from './PermissionsTable';
import { DarwinCollapse } from 'components/darwin-ui';
import DarwinTypography from 'components/darwin-ui/Typography';
import styles from './index.module.css';

interface PermissionSectionProps {
  permissions: AssignedPermission[];
}

enum PanelKeys {
  ConfigurationPermission = 'configuration-permission',
  DataCapturePermission = 'data-capture-permission',
  BillingPermission = 'billing-permission',
}

interface GroupData {
  key: PanelKeys;
  title: string;
  modules: PermissionModules[];
}

const PermissionSection = ({ permissions }: PermissionSectionProps) => {
  const { t } = useTranslation();
  const { Panel } = DarwinCollapse;
  const { Title } = DarwinTypography;

  const groupData: GroupData[] = [
    {
      key: PanelKeys.ConfigurationPermission,
      title: t('roles.role-form.configuration-permission'),
      modules: [
        PermissionModules.Organizations,
        PermissionModules.Users,
        PermissionModules.Products,
        PermissionModules.DataTables,
        PermissionModules.DataSources,
        PermissionModules.Integrations,
        PermissionModules.Forms,
        PermissionModules.LandingPages,
        PermissionModules.SiteManagement,
        PermissionModules.ProcessMaps,
        PermissionModules.RoleManagement,
      ],
    },
    {
      key: PanelKeys.DataCapturePermission,
      title: t('roles.role-form.data-capture-permission'),
      modules: [
        PermissionModules.FormsDataCapture,
        PermissionModules.RegistryManagement,
        PermissionModules.FlatFileUpload,
        PermissionModules.BlockchainLogging,
        PermissionModules.ReportingAndAnalytics,
      ],
    },
    {
      key: PanelKeys.BillingPermission,
      title: t('roles.role-form.billing-permission'),
      modules: [PermissionModules.Billing, PermissionModules.Subscriptions],
    },
  ];

  const panels = groupData.map(({ key, title, modules }) => {
    const groupPermissions = permissions.filter(p =>
      modules.includes(p.permission.module)
    );

    if (groupPermissions.length === 0) return null;

    return (
      <Panel
        header={
          <Title className={styles.permissionTableTitle} level={5}>
            {title}
          </Title>
        }
        key={key}
      >
        <RolePermissionsTable permissions={groupPermissions} />
      </Panel>
    );
  });

  return (
    <DarwinCollapse
      ghost
      defaultActiveKey={[
        PanelKeys.ConfigurationPermission,
        PanelKeys.DataCapturePermission,
        PanelKeys.BillingPermission,
      ]}
    >
      {panels}
    </DarwinCollapse>
  );
};

export default PermissionSection;
